import { formatDate } from '@/lib/orgFormat';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Search, Download } from 'lucide-react';
import { getMyLeaveRequests, cancelLeaveRequest, getMyLeaveEmployments } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { usePromptIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { leaveCancelAction, leaveStatusLabel } from './leaveCancel';
import { useC, useDark, type C as ColorSet } from '@/features/shared/theme/kaziColors';
import { HandoverNote } from '@/features/delegation/HandoverNote';
import { DELEGATION_ID_PARAM, replacementQuery, firstName } from '@/features/delegation/handover';
import { ReplaceCoveragePanel, coverNeedsReplacing } from '@/features/delegation/ReplaceCoveragePanel';
import { CoverageStatusBadge } from '@/features/delegation/CoverageStatusBadge';

interface TimeOffHistoryProps {
  /** The tab it was opened from, or 'all' (every leave type). */
  type: 'vacation' | 'sick' | 'wfh' | 'all';
  onBack: () => void;
  /** A request was withdrawn or cancelled: the page reloads what it shows
   *  (balances, the Pending requests card) — #73 approval, HO-0mvri4izu001-cf4f8b. */
  onChanged?: () => void;
}

/** Annual / Sick / WFH have dashboard tabs; every other type the employee can
 *  request (Compassionate, Maternity, …) is 'other' and shown under its own
 *  name — never filed under Annual (Muse R8, HO-0mvr6qa51001-d696cc). */
type TypeBucket = 'vacation' | 'sick' | 'wfh' | 'other';

interface HistoryRecord {
  id: string;
  type: TypeBucket;
  /** The leave type as the company named it, and its code. */
  typeCode: string;
  typeName: string;
  startDate: string;
  endDate?: string;
  days: number;
  status: 'approved' | 'pending' | 'rejected' | 'cancelled';
  reason: string;
  /** Why it was cancelled (cancel-with-reason) — shown as "Cancelled — <reason>". */
  cancellationReason?: string | null;
  /** Delegation ↔ Leave: coverage during this leave (people-first, from auth). */
  delegationId?: string | null;
  coverage?: { delegationId: string; delegateName: string | null; status: string; note: string | null } | null;
  /** Derived coverage status (leave-service) for requests still needing cover. */
  coverageStatus?: string | null;
  /** G5/R2: the company (employment) this request — or this part of a
   *  multi-employment day off — is charged to and decided by. */
  company?: string | null;
  grouped?: boolean;
  coverageReason?: string | null;
  submittedDate: string;
  approvedBy?: string;
}

const codeToType = (code?: string): TypeBucket => {
  switch ((code || '').toUpperCase()) {
    case 'ANNUAL': case 'VACATION': return 'vacation';
    case 'SICK': return 'sick';
    case 'WFH': return 'wfh';
    default: return 'other';
  }
};

/** The history filter a record belongs to: its tab, or its own type code. */
const filterKey = (r: Pick<HistoryRecord, 'type' | 'typeCode'>): string =>
  r.type === 'other' ? `code:${r.typeCode}` : r.type;

/** Tenant-scoped link straight to one delegation (the deep-link target). */
function delegationHref(id?: string | null): string {
  const base = window.location.pathname.replace(/\/app(\/.*)?$/, '/app/admin/delegations');
  return id ? `${base}?${DELEGATION_ID_PARAM}=${encodeURIComponent(id)}` : base;
}

/** "Create a new delegation" for a request that lost its cover: the new one
 *  must come back linked to THIS request, not as an unrelated delegation. */
function replacementHref(r: { id: string; startDate: string; endDate?: string | null }): string {
  const base = window.location.pathname.replace(/\/app(\/.*)?$/, '/app/admin/delegations');
  return `${base}?${replacementQuery(r.id, r.startDate.slice(0, 10), (r.endDate || r.startDate).slice(0, 10))}`;
}

const statusCfg = (C: ColorSet) => ({
  approved: { bg: C.greenLight, color: C.greenText },
  pending: { bg: C.yellowLight, color: C.yellowText },
  rejected: { bg: C.redLight, color: C.redText },
  cancelled: { bg: C.creamDark, color: C.charcoalMid },
});

export const TimeOffHistory: React.FC<TimeOffHistoryProps> = ({ type, onChanged }) => {
  const { t } = useTranslation(['timeoff']);
  const C = useC();
  const dark = useDark();
  const [searchTerm, setSearchTerm] = useState('');
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [cancelling, setCancelling] = useState<string | null>(null);
  // The tab it was opened from; any chip (or All) from there.
  const [filter, setFilter] = useState<string>(type);
  // A refused cancel, kept ON its row (Muse R4, HO-0mvq3ozg2001-08818e): a
  // toast alone was gone before anyone read it, so the refusal looked like
  // nothing happened. `final`: the server said you may not (403) — the row
  // stops offering the button; otherwise it can be tried again.
  const [refusals, setRefusals] = useState<Record<string, { message: string; final: boolean }>>({});
  // Branded reason prompt (never window.prompt). Null only when rendered
  // without the app's ConfirmProvider — then the action is simply not offered.
  const promptText = usePromptIfAvailable();

  const load = useCallback(() => {
    (async () => {
      try {
        const rows = await getMyLeaveRequests();
        // G5/R2: label each request with its employment when there are several.
        const emps = await getMyLeaveEmployments().catch(() => ({ per_employment: false, employments: [] }));
        const companyOf = new Map((emps.employments ?? []).map((e) => [e.engagement_id, e.company]));
        setHistory((Array.isArray(rows) ? rows : []).map((r: any) => ({
          company: emps.per_employment && r.engagement_id ? companyOf.get(String(r.engagement_id)) ?? null : null,
          grouped: !!r.request_group_id,
          id: String(r.id),
          type: codeToType(r.leave_type?.type_code || r.type),
          typeCode: String(r.leave_type?.type_code || r.type || '').toUpperCase(),
          typeName: r.leave_type?.name || '',
          startDate: r.start_date, endDate: r.end_date,
          days: Number(r.days_requested ?? r.days ?? 0),
          status: String(r.status || '').toLowerCase() as HistoryRecord['status'],
          reason: r.reason || '—',
          cancellationReason: r.cancellation_reason ?? null,
          submittedDate: (r.submitted_at || r.created_at || '').slice(0, 10),
          approvedBy: r.approved_by ? 'Manager' : undefined,
          delegationId: r.delegation_id ?? null,
          coverage: null,
          coverageStatus: r.coverage_status ?? null,
          coverageReason: r.coverage_reason ?? null,
        })));
        // Coverage during leave: resolve the covering delegation (delegate name +
        // status) for the few rows that have one — via the auth by-leave read.
        const withCoverage = (Array.isArray(rows) ? rows : []).filter((r: any) => r.delegation_id);
        if (withCoverage.length) {
          const { getDelegationsForLeave } = await import('@/services/rbacService');
          const results = await Promise.all(withCoverage.map((r: any) => getDelegationsForLeave(String(r.id)).catch(() => [])));
          const byId: Record<string, NonNullable<HistoryRecord['coverage']>> = {};
          withCoverage.forEach((r: any, i: number) => {
            // The delegation the leave points at, else the first one covering it.
            const d = results[i].find((x) => x.id === String(r.delegation_id)) ?? results[i][0];
            if (d) byId[String(r.id)] = { delegationId: d.id, delegateName: d.delegateName ?? null, status: String(d.status ?? ''), note: d.note ?? null };
          });
          setHistory((h) => h.map((row) => (byId[row.id] ? { ...row, coverage: byId[row.id] } : row)));
        }
      } catch { /* leave service unavailable */ }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  // Owner decision 2026-10: withdraw a pending request, or cancel approved leave
  // that has not started — always with a reason. To change dates: cancel, then
  // request again. Started leave: only the manager/HR can cancel it.
  const onCancel = async (r: HistoryRecord, action: 'withdraw' | 'cancel') => {
    if (!promptText) return;
    const withdraw = action === 'withdraw';
    const reason = await promptText({
      title: withdraw
        ? t('timeoff:withdraw_request_title', { defaultValue: 'Withdraw leave request' })
        : t('timeoff:cancel_leave_title', { defaultValue: 'Cancel approved leave' }),
      message: withdraw
        ? t('timeoff:withdraw_request_message', { defaultValue: 'Your manager will no longer be asked to review it.' })
        : t('timeoff:cancel_leave_message', {
            defaultValue: 'The days go back to your balance and your manager is told. Anyone covering for you is released. To change dates, cancel and request again.',
          }),
      label: t('timeoff:cancel_reason_label', { defaultValue: 'Reason' }),
      placeholder: t('timeoff:cancel_reason_placeholder', { defaultValue: 'e.g. Plans changed' }),
      confirmLabel: withdraw
        ? t('timeoff:withdraw_request', { defaultValue: 'Withdraw request' })
        : t('timeoff:cancel_leave', { defaultValue: 'Cancel leave' }),
      cancelLabel: t('timeoff:keep_it', { defaultValue: 'Keep it' }),
      required: true,
      multiline: true,
      danger: true,
    });
    if (reason == null || !reason.trim()) return;
    setCancelling(r.id);
    setRefusals(({ [r.id]: _, ...rest }) => rest);
    try {
      await cancelLeaveRequest(r.id, reason.trim());
      toast.success(withdraw
        ? t('timeoff:withdrawn_toast', { defaultValue: 'Request withdrawn' })
        : t('timeoff:cancelled_toast', { defaultValue: 'Leave cancelled — the days are back in your balance' }));
      load();
      onChanged?.();
    } catch (e) {
      const message = getApiErrorMessage(e, t('timeoff:cancel_failed', { defaultValue: 'Could not cancel this leave' }));
      const final = (e as { response?: { status?: number } })?.response?.status === 403;
      setRefusals((m) => ({ ...m, [r.id]: { message, final } }));
      toast.error(message, { duration: 6000 });
    } finally {
      setCancelling(null);
    }
  };

  const bucketName = (b: TypeBucket) =>
    b === 'vacation' ? t('timeoff:annual_leave', { defaultValue: 'Annual Leave' })
      : b === 'sick' ? t('timeoff:sick_leave', { defaultValue: 'Sick Leave' })
        : b === 'wfh' ? t('timeoff:work_from_home', { defaultValue: 'Work From Home' })
          : t('timeoff:other_leave', { defaultValue: 'Other leave' });
  const nameOf = (r: HistoryRecord) => r.typeName || bucketName(r.type);
  // Every type present gets a chip — including the ones without a dashboard tab.
  const chips: Array<{ key: string; label: string }> = [];
  for (const r of history) {
    const k = filterKey(r);
    if (!chips.some((c) => c.key === k)) chips.push({ key: k, label: nameOf(r) });
  }
  const selected = chips.find((c) => c.key === filter);
  const typeLabel = filter === 'all'
    ? t('timeoff:all_leave', { defaultValue: 'Leave' })
    : selected?.label ?? bucketName(filter as TypeBucket);
  const fmtRange = (s: string, e?: string) => (!e || s === e) ? formatDate(s) : `${formatDate(s)} – ${formatDate(e)}`;

  const rows = history
    .filter((r) => filter === 'all' || filterKey(r) === filter)
    .filter((r) => r.reason.toLowerCase().includes(searchTerm.toLowerCase()) || r.status.toLowerCase().includes(searchTerm.toLowerCase()));

  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.charcoalSubtle, whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '12px 16px', fontSize: 13, color: C.charcoal, verticalAlign: 'top' };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: C.charcoal, flex: 1 }}>{typeLabel} {t('timeoff:history', { defaultValue: 'History' })}</h2>
        <button type="button" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: `1px solid ${C.border}`, background: 'transparent', color: C.orange, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          <Download size={14} /> {t('timeoff:history_export', { defaultValue: 'Export' })}
        </button>
      </div>

      {chips.length > 0 && (
        <div role="group" aria-label={t('timeoff:history_type_filter', { defaultValue: 'Leave type' })} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {[{ key: 'all', label: t('timeoff:all_types', { defaultValue: 'All types' }) }, ...chips].map((c) => (
            <button key={c.key} type="button" aria-pressed={filter === c.key} onClick={() => setFilter(c.key)}
              style={{ padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${filter === c.key ? C.orange : C.border}`,
                background: filter === c.key ? C.orange : 'transparent', color: filter === c.key ? '#fff' : C.charcoalMid }}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: dark ? C.creamDeep : C.cream, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <Search size={16} color={C.charcoalLight} />
        <input placeholder={t('timeoff:history_search', { defaultValue: 'Search history…' })} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: C.charcoal, width: '100%', fontFamily: 'inherit' }} />
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: C.charcoalLight, fontSize: 13 }}>No {typeLabel.toLowerCase()} history yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: dark ? C.creamDeep : C.cream }}>
                <th style={th}>{t('timeoff:history_type', { defaultValue: 'Type' })}</th>
                <th style={th}>{t('timeoff:history_date_range', { defaultValue: 'Dates' })}</th>
                <th style={th}>{t('timeoff:history_days', { defaultValue: 'Days' })}</th>
                <th style={th}>{t('timeoff:history_reason', { defaultValue: 'Reason' })}</th>
                <th style={th}>{t('timeoff:coverage_during_leave', { defaultValue: 'Coverage during leave' })}</th>
                <th style={th}>{t('timeoff:history_status', { defaultValue: 'Status' })}</th>
                <th style={th}>{t('timeoff:history_submitted', { defaultValue: 'Submitted' })}</th>
                <th style={th}>{t('timeoff:history_approved_by', { defaultValue: 'Approved by' })}</th>
                {promptText && <th style={th}><span className="sr-only">{t('timeoff:history_actions', { defaultValue: 'Actions' })}</span></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const sc = statusCfg(C)[r.status] || statusCfg(C).cancelled;
                const refusal = refusals[r.id];
                const computed = leaveCancelAction(r.status, r.startDate, undefined, r.endDate);
                // The server is the authority: once it refused, the row shows why
                // instead of offering the same button again.
                const action = refusal?.final ? null : computed;
                return (
                  <tr key={r.id} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{nameOf(r)}</td>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtRange(r.startDate, r.endDate)}</td>
                    <td style={td}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, border: `1px solid ${C.border}`, color: C.charcoalMid }}>{r.days} {r.days !== 1 ? t('timeoff:days', { defaultValue: 'days' }) : t('timeoff:day', { defaultValue: 'day' })}</span></td>
                    <td style={{ ...td, color: C.charcoalMid, maxWidth: 240 }}>{r.reason}</td>
                    <td style={{ ...td, color: C.charcoalMid }}>
                      {r.delegationId ? (
                        <div data-testid="history-coverage">
                          {r.coverage?.delegateName || t('timeoff:coverage_delegated_to', { defaultValue: 'Delegated to' })}
                          <span style={{ display: 'block', fontSize: 11, color: ['active', 'scheduled'].includes(r.coverage?.status || '') ? '#15803D' : C.charcoalSubtle }}>
                            {['active', 'scheduled'].includes(r.coverage?.status || '')
                              ? t('timeoff:coverage_status_confirmed', { defaultValue: 'Coverage confirmed' })
                              : r.coverage?.status === 'declined'
                                ? t('timeoff:coverage_status_declined', { defaultValue: 'Coverage declined' })
                                : ['revoked', 'rejected'].includes(r.coverage?.status || '')
                                  ? t('timeoff:coverage_status_cancelled', { defaultValue: 'Coverage cancelled' })
                                  : r.coverage?.status === 'expired'
                                    ? t('timeoff:coverage_status_expired', { defaultValue: 'Coverage expired' })
                                    : t('timeoff:coverage_status_pending', { defaultValue: 'Coverage pending' })}
                          </span>
                          <CoverageStatusBadge className="mt-1" status={r.coverageStatus} reason={r.coverageReason} delegateName={r.coverage?.delegateName} />
                          {/* The handover message the requester left their delegate
                              (plain text, escaped, line breaks kept). Older leaves
                              without one simply show nothing here. */}
                          <HandoverNote
                            className="mt-1.5 max-w-xs"
                            note={r.coverage?.note}
                            label={t('timeoff:handover_your_message', { name: firstName(r.coverage?.delegateName) || t('timeoff:handover_the_delegate', { defaultValue: 'your delegate' }), defaultValue: 'Your message to {{name}}' })}
                          />
                          <a href={delegationHref(r.coverage?.delegationId ?? r.delegationId)} onClick={(e) => { e.preventDefault(); window.location.assign(delegationHref(r.coverage?.delegationId ?? r.delegationId)); }} style={{ fontSize: 11, color: C.orange }}>
                            {t('timeoff:coverage_view_delegation', { defaultValue: 'View delegation' })}
                          </a>
                          {/* Cover lost (declined / cancelled) before the leave is over:
                              pick a replacement here rather than starting again. */}
                          {coverNeedsReplacing({ status: r.status, startDate: r.startDate, endDate: r.endDate, coverageStatus: r.coverage?.status }) && (
                            <ReplaceCoveragePanel
                              requestId={r.id}
                              startDate={r.startDate.slice(0, 10)}
                              endDate={(r.endDate || r.startDate).slice(0, 10)}
                              onReplaced={load}
                              onCreateNew={() => window.location.assign(replacementHref(r))}
                            />
                          )}
                        </div>
                      ) : (
                        // No delegate named: still say what the cover status is
                        // (unassigned / needs attention), and let them name one.
                        <div>
                          {r.coverageStatus && r.coverageStatus !== 'not_required' ? (
                            <CoverageStatusBadge status={r.coverageStatus} reason={r.coverageReason} withReason />
                          ) : '—'}
                          {['needs_attention', 'unassigned'].includes(r.coverageStatus || '') && ['pending', 'approved'].includes(r.status) && (
                            <ReplaceCoveragePanel
                              requestId={r.id}
                              startDate={r.startDate.slice(0, 10)}
                              endDate={(r.endDate || r.startDate).slice(0, 10)}
                              onReplaced={load}
                              onCreateNew={() => window.location.assign(replacementHref(r))}
                            />
                          )}
                        </div>
                      )}
                    </td>
                    <td style={td}><span data-testid="history-status" style={{ display: 'inline-flex', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: sc.bg, color: sc.color, maxWidth: 260 }}>{leaveStatusLabel(r.status, r.cancellationReason)}</span>{r.company ? <div data-testid="history-company" style={{ fontSize: 11, color: C.charcoalLight, marginTop: 4 }}>{r.company}{r.grouped ? ' · part of a day off from several jobs' : ''}</div> : null}</td>
                    <td style={{ ...td, color: C.charcoalLight, whiteSpace: 'nowrap' }}>{r.submittedDate ? formatDate(r.submittedDate) : '—'}</td>
                    <td style={{ ...td, color: C.charcoalLight }}>{r.approvedBy || '—'}</td>
                    {promptText && (
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {action === 'withdraw' || action === 'cancel' ? (
                          <button
                            type="button"
                            data-testid="history-cancel"
                            disabled={cancelling === r.id}
                            onClick={() => onCancel(r, action)}
                            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.redText, fontSize: 12, fontWeight: 600, cursor: cancelling === r.id ? 'wait' : 'pointer', opacity: cancelling === r.id ? 0.6 : 1 }}
                          >
                            {action === 'withdraw'
                              ? t('timeoff:withdraw_request', { defaultValue: 'Withdraw request' })
                              : t('timeoff:cancel_leave', { defaultValue: 'Cancel leave' })}
                          </button>
                        ) : action === 'ask_manager' ? (
                          <span style={{ fontSize: 11, color: C.charcoalSubtle }}>
                            {t('timeoff:cancel_started_hint', { defaultValue: 'Started — ask your manager or HR to cancel' })}
                          </span>
                        ) : null}
                        {refusal && (
                          <div
                            role="alert"
                            data-testid="history-cancel-refused"
                            style={{ marginTop: action ? 6 : 0, maxWidth: 260, whiteSpace: 'normal', fontSize: 11, lineHeight: 1.4, color: C.redText }}
                          >
                            {refusal.message}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TimeOffHistory;
